import { darken, lighten, ThemeOptions } from "@mui/material";

import { convertHexToRGBA } from "utils/commonUtils";
import { getInvertColor } from "utils/themeUtils";

export const chipOverrides: ThemeOptions["components"] = {
	MuiChip: {
		defaultProps: {
			size: "small",
			variant: "filled",
		},
		styleOverrides: {
			root: ({ theme }) => ({
				width: "fit-content",
				height: "auto",
				borderRadius: 9999,

				"& .MuiChip-deleteIcon": {
					marginRight: 0,
					marginLeft: theme.spacing(1),
				},
				"& .MuiChip-iconSmall": {
					marginLeft: 0,
					marginRight: theme.spacing(1),
				},
				"& .MuiChip-iconMedium": {
					marginLeft: 0,
					marginRight: theme.spacing(1),
				},
				"&.Mui-disabled": {
					backgroundColor: theme.palette.dirtyWhite["600"],

					"& .MuiChip-labelSmall, & .MuiChip-labelMedium": {
						color: theme.palette.common.white,
					},
					"&.MuiChip-outlined": {
						borderColor: theme.palette.dirtyWhite["600"] + " !important",
					},
					"& .MuiChip-deleteIconContainedColorDefault, & .MuiChip-deleteIconContainedColorPrimary, & .MuiChip-deleteIconContainedColorSecondary, & .MuiChip-deleteIconContainedColorWarning, & .MuiChip-deleteIconContainedColorSuccess, & .MuiChip-deleteIconContainedColorError, & .MuiChip-deleteIconFilledColorDefault, & .MuiChip-deleteIconFilledColorPrimary, & .MuiChip-deleteIconFilledColorSecondary, & .MuiChip-deleteIconFilledColorWarning, & .MuiChip-deleteIconFilledColorSuccess, & .MuiChip-deleteIconFilledColorError, & .MuiChip-deleteIconOutlinedColorDefault, & .MuiChip-deleteIconOutlinedColorPrimary, & .MuiChip-deleteIconOutlinedColorSecondary, & .MuiChip-deleteIconOutlinedColorWarning, & .MuiChip-deleteIconOutlinedColorSuccess, & .MuiChip-deleteIconOutlinedColorError, & .MuiChip-icon": {
						color: theme.palette.common.white + " !important",
					},
				},
			}),

			sizeSmall: ({ theme }) => ({
				paddingTop: theme.spacing(1),
				paddingBottom: theme.spacing(1),
				paddingLeft: theme.spacing(4),
				paddingRight: theme.spacing(4),
			}),

			labelSmall: ({ theme }) => ({
				paddingLeft: 0,
				paddingRight: 0,
				fontWeight: 700,
				fontSize: theme.typography.pxToRem(14),
				lineHeight: "142.857%",
			}),

			sizeXs: ({ theme }) => ({
				paddingTop: theme.spacing(1),
				paddingBottom: theme.spacing(1),
				paddingLeft: theme.spacing(2),
				paddingRight: theme.spacing(2),
			}),

			labelXs: ({ theme }) => ({
				paddingLeft: 0,
				paddingRight: 0,
				fontWeight: 600,
				fontSize: theme.typography.pxToRem(12),
				lineHeight: "133.333%",
			}),

			contained: ({ theme }) => {
				return {
					color: theme.palette.dirtyWhite["2000"],
					backgroundColor: theme.palette.dirtyWhite["200"],

					"& .MuiChip-deleteIconContainedColorDefault": {
						"&:hover": {
							color: theme.palette.dirtyWhite["2000"],
						},
					},
					"& .MuiChip-icon, & .MuiChip-deleteIconContainedColorDefault": {
						color: convertHexToRGBA(theme.palette.dirtyWhite["2000"], 0.7),
					},
				};
			},

			containedPrimary: ({ theme }) => {
				const mainColor = theme.palette.primary.main;

				return {
					color: getInvertColor(mainColor),
					backgroundColor: mainColor,

					"& .MuiChip-deleteIconContainedColorPrimary": {
						color: convertHexToRGBA(getInvertColor(mainColor), 0.7),

						"&:hover": {
							color: theme.palette.common.white,
						},
					},
					"& .MuiChip-icon": {
						color: getInvertColor(mainColor),
					},
				};
			},

			containedSecondary: ({ theme }) => {
				const mainColor = theme.palette.secondary.main;

				return {
					color: getInvertColor(mainColor),
					backgroundColor: mainColor,

					"& .MuiChip-deleteIconContainedColorSecondary": {
						color: convertHexToRGBA(getInvertColor(mainColor), 0.7),

						"&:hover": {
							color: theme.palette.common.white,
						},
					},
					"& .MuiChip-icon": {
						color: getInvertColor(mainColor),
					},
				};
			},

			containedSuccess: ({ theme }) => ({
				color: theme.palette.common.white,
				backgroundColor: theme.palette.success.main,

				"& .MuiChip-deleteIconContainedColorSuccess": {
					color: convertHexToRGBA(theme.palette.common.white, 0.7),
					"&:hover": {
						color: theme.palette.common.white,
					},
				},
				"& .MuiChip-icon": {
					color: theme.palette.common.white,
				},
			}),

			containedError: ({ theme }) => ({
				color: theme.palette.common.white,
				backgroundColor: theme.palette.error.main,

				"& .MuiChip-deleteIconContainedColorError": {
					color: convertHexToRGBA(theme.palette.common.white, 0.7),
					"&:hover": {
						color: theme.palette.common.white,
					},
				},
				"& .MuiChip-icon": {
					color: theme.palette.common.white,
				},
			}),

			containedWarning: ({ theme }) => ({
				color: theme.palette.common.white,
				backgroundColor: theme.palette.warning.main,

				"& .MuiChip-deleteIconContainedColorWarning": {
					color: convertHexToRGBA(theme.palette.common.white, 0.7),
					"&:hover": {
						color: theme.palette.common.white,
					},
				},
				"& .MuiChip-icon": {
					color: theme.palette.common.white,
				},
			}),

			// default.
			filled: ({ theme }) => ({
				color: theme.palette.dirtyWhite["2000"],
				backgroundColor: theme.palette.dirtyWhite["200"],

				"& .MuiChip-deleteIconFilledColorDefault": {
					"&:hover": {
						color: theme.palette.dirtyWhite["2000"],
					},
				},
				"& .MuiChip-icon, & .MuiChip-deleteIconFilledColorDefault": {
					color: convertHexToRGBA(theme.palette.dirtyWhite["2000"], 0.7),
				},
			}),

			filledPrimary: ({ theme }) => {
				const mainColor = theme.palette.primary.main;
				const isLight = getInvertColor(mainColor) === theme.palette.dirtyWhite["2000"];

				return {
					color: isLight ? darken(mainColor, 0.5) : mainColor,
					backgroundColor: isLight ? lighten(mainColor, 0.5) : lighten(mainColor, 0.9),

					"&.MuiChip-clickableColorPrimary": {
						"&:hover": {
							color: getInvertColor(mainColor),
						},
					},
					"& .MuiChip-deleteIconFilledColorPrimary": {
						"&:hover": {
							color: isLight ? darken(mainColor, 0.8) : mainColor,
						},
					},
					"& .MuiChip-icon, & .MuiChip-deleteIconFilledColorPrimary": {
						color: isLight ? darken(mainColor, 0.5) : convertHexToRGBA(mainColor, 0.7),
					},
				};
			},

			filledSecondary: ({ theme }) => {
				const mainColor = theme.palette.secondary.main;
				const isLight = getInvertColor(mainColor) === theme.palette.dirtyWhite["2000"];

				return {
					color: isLight ? darken(mainColor, 0.5) : mainColor,
					backgroundColor: isLight ? lighten(mainColor, 0.5) : lighten(mainColor, 0.9),

					"&.MuiChip-clickableColorSecondary": {
						"&:hover": {
							color: getInvertColor(mainColor),
						},
					},
					"& .MuiChip-deleteIconFilledColorSecondary": {
						"&:hover": {
							color: isLight ? darken(mainColor, 0.8) : mainColor,
						},
					},
					"& .MuiChip-icon, & .MuiChip-deleteIconFilledColorSecondary": {
						color: isLight ? darken(mainColor, 0.5) : convertHexToRGBA(mainColor, 0.7),
					},
				};
			},

			filledSuccess: ({ theme }) => ({
				color: theme.palette.success.main,
				backgroundColor: theme.palette.success.light,

				"&.MuiChip-clickableColorSuccess": {
					"&:hover": {
						color: theme.palette.common.white,
					},
				},
				"& .MuiChip-deleteIconFilledColorSuccess": {
					"&:hover": {
						color: theme.palette.success.main,
					},
				},
				"& .MuiChip-icon, & .MuiChip-deleteIconFilledColorSuccess": {
					color: convertHexToRGBA(theme.palette.success.main, 0.7),
				},
			}),

			filledError: ({ theme }) => ({
				color: theme.palette.error.main,
				backgroundColor: theme.palette.error.light,

				"&.MuiChip-clickableColorError": {
					"&:hover": {
						color: theme.palette.common.white,
					},
				},
				"& .MuiChip-deleteIconFilledColorError": {
					"&:hover": {
						color: theme.palette.error.main,
					},
				},
				"& .MuiChip-icon, & .MuiChip-deleteIconFilledColorError": {
					color: convertHexToRGBA(theme.palette.error.main, 0.7),
				},
			}),

			filledWarning: ({ theme }) => ({
				color: theme.palette.warning.main,
				backgroundColor: theme.palette.warning.light,

				"&.MuiChip-clickableColorWarning": {
					"&:hover": {
						color: theme.palette.common.white,
					},
				},
				"& .MuiChip-deleteIconFilledColorWarning": {
					"&:hover": {
						color: theme.palette.warning.main,
					},
				},
				"& .MuiChip-icon, & .MuiChip-deleteIconFilledColorWarning": {
					color: convertHexToRGBA(theme.palette.warning.main, 0.7),
				},
			}),

			// default.
			outlined: ({ theme }) => ({
				color: theme.palette.dirtyWhite["2000"],
				backgroundColor: theme.palette.dirtyWhite["200"],
				borderColor: theme.palette.dirtyWhite["1500"],

				"& .MuiChip-deleteIconOutlinedColorDefault": {
					"&:hover": {
						color: theme.palette.dirtyWhite["2000"],
					},
				},
				"& .MuiChip-icon, & .MuiChip-deleteIconOutlinedColorDefault": {
					color: convertHexToRGBA(theme.palette.dirtyWhite["2000"], 0.7),
				},
			}),

			outlinedPrimary: ({ theme }) => {
				const mainColor = theme.palette.primary.main;
				const isLight = getInvertColor(mainColor) === theme.palette.dirtyWhite["2000"];

				return {
					color: isLight ? darken(mainColor, 0.5) : mainColor,
					backgroundColor: isLight ? lighten(mainColor, 0.5) : lighten(mainColor, 0.9),
					borderColor: isLight ? darken(mainColor, 0.5) : mainColor,

					"& .MuiChip-deleteIconOutlinedColorPrimary": {
						"&:hover": {
							color: isLight ? darken(mainColor, 0.8) : mainColor,
						},
					},
					"& .MuiChip-icon, & .MuiChip-deleteIconOutlinedColorPrimary": {
						color: isLight ? darken(mainColor, 0.5) : convertHexToRGBA(mainColor, 0.7),
					},
				};
			},

			outlinedSecondary: ({ theme }) => {
				const mainColor = theme.palette.secondary.main;
				const isLight = getInvertColor(mainColor) === theme.palette.dirtyWhite["2000"];

				return {
					color: isLight ? darken(mainColor, 0.5) : mainColor,
					backgroundColor: isLight ? lighten(mainColor, 0.5) : lighten(mainColor, 0.9),
					borderColor: isLight ? darken(mainColor, 0.5) : mainColor,

					"& .MuiChip-deleteIconOutlinedColorSecondary": {
						"&:hover": {
							color: isLight ? darken(mainColor, 0.8) : mainColor,
						},
					},
					"& .MuiChip-icon, & .MuiChip-deleteIconOutlinedColorSecondary": {
						color: isLight ? darken(mainColor, 0.5) : convertHexToRGBA(mainColor, 0.7),
					},
				};
			},

			outlinedSuccess: ({ theme }) => ({
				color: theme.palette.success.main,
				backgroundColor: theme.palette.success.light,
				borderColor: theme.palette.success.main,

				"& .MuiChip-deleteIconOutlinedColorSuccess": {
					"&:hover": {
						color: theme.palette.success.main,
					},
				},
				"& .MuiChip-icon, & .MuiChip-deleteIconOutlinedColorSuccess": {
					color: convertHexToRGBA(theme.palette.success.main, 0.7),
				},
			}),

			outlinedError: ({ theme }) => ({
				color: theme.palette.error.main,
				backgroundColor: theme.palette.error.light,
				borderColor: theme.palette.error.main,

				"& .MuiChip-deleteIconOutlinedColorError": {
					"&:hover": {
						color: theme.palette.error.main,
					},
				},
				"& .MuiChip-icon, & .MuiChip-deleteIconOutlinedColorError": {
					color: convertHexToRGBA(theme.palette.error.main, 0.7),
				},
			}),

			outlinedWarning: ({ theme }) => ({
				color: theme.palette.warning.main,
				backgroundColor: theme.palette.warning.light,
				borderColor: theme.palette.warning.main,

				"& .MuiChip-deleteIconOutlinedColorWarning": {
					"&:hover": {
						color: theme.palette.warning.main,
					},
				},
				"& .MuiChip-icon, & .MuiChip-deleteIconOutlinedColorWarning": {
					color: convertHexToRGBA(theme.palette.warning.main, 0.7),
				},
			}),

			// we only have 1 size in the playbook, so this is for backwards compatibility only.
			sizeMedium: ({ theme }) => ({
				paddingTop: theme.spacing(2),
				paddingBottom: theme.spacing(2),
				paddingLeft: theme.spacing(4),
				paddingRight: theme.spacing(4),
			}),

			labelMedium: ({ theme }) => ({
				paddingLeft: 0,
				paddingRight: 0,
				fontWeight: 700,
				fontSize: theme.typography.pxToRem(16),
				lineHeight: "150%",
			}),
		},
	},
};
